import { Menu } from '@mantine/core';
import { SettingsButton } from '@repo/common/components';
import { UserRole } from '@repo/common/enums/UserRole';
import { Button, Text } from '@repo/foundations';
import { map } from 'lodash-es';
import { useState } from 'react';
import { useSuspenseFeatureAccess } from '../../../api';
import type { BackgroundJob } from '../../../lib/models';
import { downloadExportTemplate, exportData } from '../../../lib/models';
import { useCheckIAMPermission } from '../../../utils/authorization/roles';
import { snakeCaseToTitleCase } from '../../../utils/shared.utils';
import { UpgradeButton } from '../UpgradeButton';

const TYPES_TO_EXPORT = [
	'table',
	'column',
	'dashboard',
	'chart',
	'job',
	'event',
	'glossary',
	'document',
	'tag',
];

interface ExportSettingsProps {
	setBackgroundJob: (backgroundJob: BackgroundJob | null) => void;
}

export function ExportSettings({ setBackgroundJob }: ExportSettingsProps) {
	const { hasPermission } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'ImportExports.Write',
	});
	const { exportAccess } = useSuspenseFeatureAccess();

	const [opened, setOpened] = useState(false);
	const disabled = !hasPermission || !exportAccess;

	return (
		<SettingsButton
			title="Export"
			description={
				<Text size="xs" color="text/secondary/default">
					Bulk export resource descriptions and custom properties{' '}
					<Button onClick={downloadExportTemplate} variant="plain">
						Download sample
					</Button>
				</Text>
			}
			buttonText="Export data"
			onClick={() => setOpened(true)}
		>
			{exportAccess ? (
				<Menu
					opened={opened}
					onChange={setOpened}
					position="bottom-start"
					width={150}
				>
					<Menu.Target>
						<Button
							variant="default"
							disabled={disabled}
							rightIconName="chevronDown"
						>
							Export data
						</Button>
					</Menu.Target>
					<Menu.Dropdown>
						{map(TYPES_TO_EXPORT, (type: string) => (
							<Menu.Item
								key={type}
								onClick={() => {
									exportData(type).then((newBackgroundJob) => {
										setBackgroundJob(newBackgroundJob);
									});
								}}
							>
								{`${snakeCaseToTitleCase(type)} resources`}
							</Menu.Item>
						))}
					</Menu.Dropdown>
				</Menu>
			) : (
				<UpgradeButton feature="Export" />
			)}
		</SettingsButton>
	);
}
